import React, { useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import CloseIcon from "@mui/icons-material/Close";

const formatCurrency = (amountInCents, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format((amountInCents || 0) / 100);

const statusColor = (status) => {
  const key = String(status || "").toLowerCase();
  if (["processing"].includes(key)) return "info";
  if (["succeeded", "paid"].includes(key)) return "success";
  if (["failed", "canceled"].includes(key)) return "error";
  return "default";
};

const statusLabel = (status) => String(status || "unknown").replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());

const Payments = ({ embedded = false, loading, payments, page, rowsPerPage, onPageChange, onRowsPerPageChange }) => {
  const [selectedPayment, setSelectedPayment] = useState(null);
  const pagedPayments = payments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const content = (
    <>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
        <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: "rgba(46, 125, 50, 0.1)", color: "success.main" }}>
          <PaidIcon fontSize="small" />
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 850, fontSize: 18 }}>Payments</Typography>
          <Typography variant="body2" color="text.secondary">
            View successful charges and payment details.
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : pagedPayments.length === 0 ? (
        <Box sx={{ flexGrow: 1, minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">No payments found.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedPayments.map((p) => (
                <TableRow key={p.id} hover onClick={() => setSelectedPayment(p)} sx={{ cursor: "pointer" }}>
                  <TableCell>{p.created ? new Date(p.created * 1000).toLocaleDateString() : "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabel(p.status)}
                      color={statusColor(p.status)}
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography variant="body2" noWrap title={p.description || ""}>
                      {p.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(p.amount || 0, p.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {payments.length > 0 && (
        <Box sx={{ mt: "auto", pt: 0.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={payments.length}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Show"
          />
          <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
        </Box>
      )}
    </>
  );

  return (
    <>
      {embedded ? (
        <Box>{content}</Box>
      ) : (
        <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, mb: 2, p: 2 }}>
          {content}
        </Paper>
      )}

      <Dialog open={!!selectedPayment} onClose={() => setSelectedPayment(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Payment Details
          </Typography>
          <IconButton edge="end" sx={{ ml: 'auto' }} onClick={() => setSelectedPayment(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!!selectedPayment && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Paper variant="outlined" sx={{ p: 1.5, border: 'none' }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2">Date</Typography>
                    <Typography variant="body2">
                      {selectedPayment.created ? new Date(selectedPayment.created * 1000).toLocaleString() : "-"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Status</Typography>
                    <Chip
                      label={statusLabel(selectedPayment.status)}
                      color={statusColor(selectedPayment.status)}
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2">Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatCurrency(selectedPayment.amount || 0, selectedPayment.currency)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2">Amount Received</Typography>
                    <Typography variant="body2">
                      {formatCurrency(selectedPayment.amount_received || 0, selectedPayment.currency)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle2">Description</Typography>
                    <Typography variant="body2" sx={{ maxWidth: "65%", textAlign: "right" }}>
                      {selectedPayment.description || "-"}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
              {/* <Divider/>
              <Paper variant="outlined" sx={{ p: 1.5, border: 'none' }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2">Payment Ref</Typography>
                    <Typography variant="body2">
                      <Chip
                        label={selectedPayment.id || "-"}
                        color="default"
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2">Invoice Ref</Typography>
                    <Typography variant="body2">
                      <Chip
                        label={selectedPayment.invoice || "-"}
                        color="default"
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    </Typography>
                  </Stack>
                </Stack>
              </Paper> */}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Payments;
